import React, { memo } from 'react'
import { NextPage } from 'next'
import { Stack, useProductsQuery } from '@thirstycamel/ui'

import LayoutMain from '../layout/LayoutMain'
import SpecialsSection from '../components/SpecialsSection'
import HumpClubBanner from '../components/HumpClubBanner'
import HomeBanner from '../components/HomeBanner'
import Advert from '../components/Advert'
import IndexSeoContent from '../components/IndexSeoContent'
import { Context } from './_app'
import { QueryCache, useQuery } from 'react-query'
import { dehydrate } from 'react-query/hydration'
import { useStore } from '../store/hooks'
import type CoreProductEntity from '@ts/core/src/modules/coreproduct/coreproduct.entity'
import imageProxy from '../utils/imageProxy'
import { buildHomepageSchema } from '../utils/schema'

import geoip from 'geoip-lite'
import requestIp from 'request-ip'
import { useUser } from '../hooks/useUser'
import { useApp } from '../hooks/useApp'
import { backend } from '../utils/backend'
import { cmsUseNew } from '../utils/cms'
import { fetchGraphQL } from '../utils/graphql-fetch'
import { BannersDocument } from '../../generated'

// This should be controlled via Portal
const sections = [
  {
    title: 'Beer',
    slug: 'beer',
  },
  {
    title: 'Premix',
    slug: 'premix',
  },
  {
    title: 'Spirits',
    slug: 'spirits',
  },
  {
    title: 'Red Wine',
    slug: 'red-wine',
  },
  {
    title: 'White Wine',
    slug: 'white-wine',
  },
]

interface IndexPageProps {
  indexSeoCopy?: string
}

const Index: NextPage<IndexPageProps> = memo(({ indexSeoCopy }) => {
  const selectedStore = useStore(s => s.store.selectedStore)
  const location = useStore(store => store.location)
  const { isLoggedIn } = useUser()
  const { isMobileApp, pushToken, platform } = useApp()

  const data = useProductsQuery<CoreProductEntity>({
    group: 'beer',
    store: selectedStore?.slug,
    region: selectedStore?.slug ? undefined : location.region,
  })

  const region = selectedStore?.region || location.region || 'VIC'

  const useNewCms = cmsUseNew()

  const coreBannerQuery = useQuery<any, any>(
    ['banners/public', 'HOME_CAROUSEL', region],
    () => backend('banners/public', { params: { placement: 'HOME_CAROUSEL', region } }),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      staleTime: 30000,
      enabled: useNewCms,
    },
  )

  const wpRegion = region === 'NSWBR' ? 'NSW_BR' : region

  const wpBannerQuery = useQuery<any, any>(
    ['api/cms', BannersDocument, { first: 10, region: wpRegion }],
    fetchGraphQL,
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      staleTime: 30000,
      enabled: !useNewCms,
    },
  )

  let images =
    (useNewCms
      ? coreBannerQuery?.data?.map(banner => imageProxy(banner?.imageDesktop?.filename))
      : wpBannerQuery?.data?.banners?.edges?.map(({ node }) =>
          imageProxy(node?.acf?.imageDesktop?.sourceUrl, { wp: true }),
        )
    )?.filter(Boolean) || []

  data.data?.results.items.forEach(i => {
    images = images.concat(i.media.map(m => imageProxy(m.filename, { height: 240 })))
  })

  // Home page SEO meta tags
  const meta_title = 'Thirsty Camel - Local Bottle Shop in Australia | Buy Alcohol Online'
  const meta_description =
    'Thirsty Camel bottle shop offers the best prices for beer, wine & spirits. Join the Hump Club for member-only weekly discounts.'
  const pageTitle = 'Thirsty Camel - Local Bottle Shop in Australia | Buy Alcohol Online'
  const structuredData = buildHomepageSchema()

  return (
    <LayoutMain
      noContainer
      images={images}
      title={pageTitle}
      description={meta_description}
      meta_title={meta_title}
      meta_description={meta_description}
      ogTitle={pageTitle}
      ogDescription={meta_description}
      canonicalUrl="https://www.thirstycamel.com.au/"
      structuredData={structuredData}
    >
      <Stack spacing={12} pb={20}>
        <HomeBanner />

        {/* <Text>isApp: {isMobileApp}</Text>
        <Text>platform: {platform}</Text> */}

        <Stack spacing={[8, 10, 12]} pt={[8, 10, 12]}>
          <SpecialsSection {...sections[0]} />

          <Advert position="HOME_TOP" />

          {!isLoggedIn && <HumpClubBanner />}

          {sections.slice(1).map((section, index) => (
            <Stack spacing={[8, 10, 12]}>
              <SpecialsSection
                {...section}
                key={section.slug}
                bg={index % 2 === 0 ? 'white' : 'gray.50'}
                borderTop={index % 2 === 0 ? 0 : '1px'}
                borderBottom={index % 2 === 0 ? 0 : '1px'}
                borderColor="gray.200"
                py={index % 2 === 0 ? null : [8, 10, 12]}
              />

              {index === 0 && <Advert position="HOME_MIDDLE" />}
              {index === 1 && <Advert position="HOME_BOTTOM" />}
            </Stack>
          ))}
        </Stack>
      </Stack>
      <IndexSeoContent content={indexSeoCopy} />
    </LayoutMain>
  )
})

export const getServerSideProps = async (context: Context) => {
  const queryCache = new QueryCache()
  const indexSeoCopy = await backend<{ value: string | null }>('/config/index-seo-copy')
    .then(({ value }) => value)
    .catch(() => null)

  const ip = requestIp.getClientIp(context.req)
  const result = geoip.lookup(ip)
  let region = result?.region || 'VIC' // Default to VIC

  const validRegions = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT']

  if (!validRegions.includes(region)) {
    region = 'VIC'
  }

  // await Promise.all([
  //   queryCache.prefetchQuery(
  //     [
  //       'products/search',
  //       {
  //         params: {
  //           page: 1,
  //           limit: 12,
  //           store: selected_store,
  //           group: 'beer',
  //           region: selected_store ? undefined : region,
  //         },
  //       },
  //     ],
  //     backend,
  //     {
  //       staleTime: 30000,
  //     },
  //   ),

  //   queryCache.prefetchQuery<any, any>(
  //     ['api/cms', BannersDocument, { first: 10, region }],
  //     async (path, ...args) => {
  //       //@ts-ignore
  //       return fetchGraphQL('http://localhost:3000/api/cms', ...args)
  //     },
  //     { staleTime: 30000 },
  //     {},
  //   ),

  //   queryCache.prefetchQuery<any, any>(
  //     ['api/cms', SearchTagsDocument, { first: 5, region }],
  //     async (path, ...args) => {
  //       //@ts-ignore
  //       return fetchGraphQL('http://localhost:3000/api/cms', ...args)
  //     },
  //     { staleTime: 30000 },
  //     {},
  //   ),
  // ])

  return {
    props: {
      dehydratedState: dehydrate(queryCache),
      indexSeoCopy,
    },
  }
}

export default Index
